import React, { useEffect, useRef, useState, createContext, useContext } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import InstituteSeo from "@/components/InstituteSeo";
import api from "@/lib/api";
import { slugify } from "./_shared";
import AdmissionInquiryModal from "./AdmissionInquiryModal";
import InstituteSwitcher from "@/components/InstituteSwitcher";
import AboutMenu from "@/components/AboutMenu";
import {
  Phone, Mail, MapPin, ExternalLink, Menu, X,
  Facebook, Instagram, Linkedin, Twitter, Youtube, Leaf, ArrowUpRight,
  ChevronDown,
} from "lucide-react";

const SLUG = "foodtech";

const Ctx = createContext(null);
export const useFood = () => useContext(Ctx);

// Nav: flat structure matching /polyaurangabad and /mitest
const NAV = [
  { to: "", end: true, label: "Home" },
  { to: "about", label: "About" },
  { to: "departments", label: "Departments" },
  { to: "campus-engagements", label: "Campus Engagements" },
  { to: "committees", label: "Committees & Cells" },
  { to: "gallery", label: "Gallery" },
  { to: "facilities", label: "Facilities" },
  { to: "academics", label: "Academics" },
  { to: "alumni", label: "Alumni" },
  { to: "careers", label: "Careers" },
  { to: "fra-ffc", label: "FRA/FFC" },
  { to: "contact", label: "Contact" },
];

// NAAC has been moved to the top bar (next to the Student ERP pill), so it
// no longer appears in the main nav. buildNav is kept as a passthrough to
// preserve the render structure.
function buildNav() {
  return NAV;
}

export default function FoodLayout() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [inquiryOpen, setInquiryOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/public/website/${SLUG}`);
        if (!cancelled) setData(data);
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || "Website not configured");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "instant" });
    setMobileOpen(false);
  }, [location.pathname]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FCFAF4] flex items-center justify-center text-[#1F6B3F]">
        <Leaf className="animate-pulse" size={40} strokeWidth={1.5} />
      </div>
    );
  }
  if (err || !data) {
    return (
      <div className="min-h-screen bg-[#FCFAF4] flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <Leaf className="mx-auto text-[#1F6B3F]" size={36} strokeWidth={1.5} />
          <h1 className="mt-4 font-serif text-2xl text-[#0F1F17]">Website unavailable</h1>
          <p className="mt-2 text-sm text-[#4D5A52]">{err || "Please try again later."}</p>
        </div>
      </div>
    );
  }

  const inst = data.institute || {};
  const logoSrc = inst.logo_url || "/assets/mitcsn-logo.png";

  return (
    <Ctx.Provider value={data}>
      <InstituteSeo slug="foodtech" inst={inst} themeColor="#0F1F17" ogImage="https://gsmandal.org/assets/og/mit-foodtech-og.jpg" />

      <div className="min-h-screen flex flex-col bg-[#FCFAF4] text-[#0F1F17]">
        {/* Utility top bar */}
        <div className="bg-[#0F1F17] text-white/85 text-[12px]">
          <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-5 flex-wrap">
              {inst.admission_phone && (
                <a href={`tel:${inst.admission_phone.replace(/\s+/g, "")}`} className="inline-flex items-center gap-1.5 hover:text-[#F4751F]" data-testid="ft-top-phone">
                  <Phone size={11} strokeWidth={1.8} className="text-[#F4751F]" />
                  {inst.admission_phone}
                </a>
              )}
              {inst.email && (
                <a href={`mailto:${inst.email}`} className="inline-flex items-center gap-1.5 hover:text-[#F4751F]" data-testid="ft-top-email">
                  <Mail size={11} strokeWidth={1.8} className="text-[#F4751F]" />
                  {inst.email}
                </a>
              )}
              {inst.city && (
                <span className="hidden md:inline-flex items-center gap-1.5">
                  <MapPin size={11} strokeWidth={1.8} className="text-[#F4751F]" />
                  {inst.city}
                </span>
              )}
            </div>
            <div className="flex items-center gap-4">
              <InstituteSwitcher currentSlug="foodtech" testidPrefix="ft-top-switcher" />
              {inst.facebook_url && (<a href={inst.facebook_url} target="_blank" rel="noopener noreferrer" data-testid="ft-top-facebook" className="hover:text-[#F4751F]"><Facebook size={12} strokeWidth={1.8} /></a>)}
              {inst.instagram_url && (<a href={inst.instagram_url} target="_blank" rel="noopener noreferrer" data-testid="ft-top-instagram" className="hover:text-[#F4751F]"><Instagram size={12} strokeWidth={1.8} /></a>)}
              {inst.linkedin_url && (<a href={inst.linkedin_url} target="_blank" rel="noopener noreferrer" data-testid="ft-top-linkedin" className="hover:text-[#F4751F]"><Linkedin size={12} strokeWidth={1.8} /></a>)}
              {inst.twitter_url && (<a href={inst.twitter_url} target="_blank" rel="noopener noreferrer" data-testid="ft-top-twitter" className="hover:text-[#F4751F]"><Twitter size={12} strokeWidth={1.8} /></a>)}
              {inst.youtube_url && (<a href={inst.youtube_url} target="_blank" rel="noopener noreferrer" data-testid="ft-top-youtube" className="hover:text-[#F4751F]"><Youtube size={12} strokeWidth={1.8} /></a>)}
              <a href="https://erp.mit.asia" target="_blank" rel="noopener noreferrer" data-testid="ft-top-erp"
                 className="inline-flex items-center gap-1 bg-[#F4751F] hover:bg-[#E36213] text-white px-2.5 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-semibold">
                Student ERP <ArrowUpRight size={10} strokeWidth={2.5} />
              </a>
              {(data?.naac_documents || []).length > 0 && (
                <NavLink to="naac" data-testid="ft-top-naac"
                  className="inline-flex items-center gap-1 bg-[#FFD9A8] hover:bg-[#FFC988] text-[#0F1F17] px-2.5 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-bold ring-1 ring-[#F4751F]/40">
                  NAAC
                </NavLink>
              )}
            </div>
          </div>
        </div>

        {/* Header band */}
        <header className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-[#E6E2D6]">
          <div className="max-w-7xl mx-auto px-4 py-3 grid grid-cols-1 md:grid-cols-[minmax(0,19fr)_minmax(0,63fr)_minmax(0,18fr)] items-center gap-4">
            <NavLink to="" end className="flex items-center justify-center md:justify-start" data-testid="ft-logo-link">
              <img src={logoSrc} alt={inst.name} className="h-[96px] md:h-[112px] w-auto max-w-full object-contain" />
            </NavLink>
            <div className="text-center leading-tight">
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#1F6B3F] font-semibold" data-testid="ft-header-trust">
                Gramaudyogik Shikshan Mandal&apos;s
              </div>
              <div className="font-serif text-xl md:text-3xl text-[#0F1F17] font-bold uppercase mt-1" data-testid="ft-header-name">
                {inst.name}
              </div>
              {inst.city && (
                <div className="font-serif text-base md:text-lg text-[#0F1F17] mt-1" data-testid="ft-header-city">
                  {inst.city}
                </div>
              )}
              {inst.tagline && (
                <div className="text-[10px] md:text-xs tracking-[0.3em] uppercase text-[#C95507] italic mt-2">
                  {inst.tagline}
                </div>
              )}
            </div>
            <div className="hidden md:block text-right">
              {inst.code && (
                <div className="text-[10px] tracking-[0.25em] uppercase text-[#4D5A52]" data-testid="ft-header-code">
                  <span className="text-[#13532E] font-semibold">Institute Code</span> · {inst.code}
                </div>
              )}
              <div className="text-xs text-[#4D5A52] mt-0.5">
                MCAER Approved{inst.year_of_establishment ? ` · Estd. ${inst.year_of_establishment}` : ""}
              </div>
              {inst.mandatory_disclosure_url && (
                <a href={inst.mandatory_disclosure_url} target="_blank" rel="noopener noreferrer"
                   data-testid="ft-header-mandatory-disclosure"
                   className="mt-1 inline-flex items-center gap-1 text-[10px] tracking-[0.2em] uppercase font-semibold text-[#C95507] hover:text-[#1F6B3F] underline underline-offset-2">
                  Mandatory Disclosure <ExternalLink size={10} strokeWidth={2} />
                </a>
              )}
            </div>
          </div>

          {/* Main nav — green strip with dropdowns */}
          <nav className="bg-[#1F6B3F] shadow-md" data-testid="ft-nav">
            <div className="max-w-7xl mx-auto px-4">
              <div className="hidden xl:flex items-center gap-1 py-2 flex-wrap">
                {buildNav(data).map((n) =>
                  n.to === "departments" ? (
                    <DepartmentsDropdown key="departments" departments={data.departments || []} />
                  ) : n.to === "campus-engagements" ? (
                    <CampusEngagementsDropdown key="campus-engagements" events={data.events || []} />
                  ) : n.to === "about" ? (
                    <AboutMenu
                      key="about"
                      linkClass={navLinkClass}
                      testidPrefix="ft-nav-about"
                      menuClassName="bg-white text-slate-800 ring-1 ring-slate-200"
                      itemHoverClassName="hover:bg-orange-50 text-slate-800"
                    />
                  ) : (
                    <NavLink
                      key={n.to}
                      to={n.to}
                      end={n.end}
                      data-testid={`ft-nav-${n.to || "home"}`}
                      className={n.highlight ? navLinkAccentClass : navLinkClass}
                    >
                      {n.label}
                    </NavLink>
                  )
                )}
              </div>
              <div className="xl:hidden flex items-center justify-between py-3">
                <span className="text-white font-medium text-sm">Menu</span>
                <button
                  type="button"
                  onClick={() => setMobileOpen((v) => !v)}
                  className="p-2 text-white"
                  aria-label="Toggle menu"
                  data-testid="ft-nav-toggle"
                >
                  {mobileOpen ? <X size={20} /> : <Menu size={20} />}
                </button>
              </div>
              {mobileOpen && (
                <div className="xl:hidden grid grid-cols-2 gap-1 pb-3">
                  {buildNav(data).map((n) =>
                    n.to === "departments" ? (
                      <React.Fragment key="departments">
                        <NavLink to="departments" onClick={() => setMobileOpen(false)} className={navLinkClass}>
                          Departments
                        </NavLink>
                        {(data.departments || []).length > 0 && (
                          <div className="col-span-2 -mt-1 mb-1 pl-4 grid grid-cols-1 gap-0.5">
                            {(data.departments || []).map((d) => (
                              <NavLink
                                key={d.id}
                                to={`departments/${slugify(d.name)}`}
                                onClick={() => setMobileOpen(false)}
                                className="text-[12px] text-white/80 hover:text-[#FFD9A8] py-1.5 border-l-2 border-white/15 pl-3"
                              >
                                {d.name}
                              </NavLink>
                            ))}
                          </div>
                        )}
                      </React.Fragment>
                    ) : n.to === "campus-engagements" ? (
                      <React.Fragment key="campus-engagements">
                        <NavLink to="campus-engagements" onClick={() => setMobileOpen(false)} className={navLinkClass}>
                          Campus Engagements
                        </NavLink>
                        {(() => {
                          const types = uniqueTypesWithCount(data.events || []);
                          return types.length > 0 ? (
                            <div className="col-span-2 -mt-1 mb-1 pl-4 grid grid-cols-1 gap-0.5">
                              {types.map((t) => (
                                <NavLink
                                  key={t.slug}
                                  to={`campus-engagements/${t.slug}`}
                                  onClick={() => setMobileOpen(false)}
                                  className="text-[12px] text-white/80 hover:text-[#FFD9A8] py-1.5 border-l-2 border-white/15 pl-3"
                                >
                                  {t.label} ({t.count})
                                </NavLink>
                              ))}
                            </div>
                          ) : null;
                        })()}
                      </React.Fragment>
                    ) : n.to === "about" ? (
                      <AboutMenu
                        key="about"
                        linkClass={navLinkClass}
                        testidPrefix="ft-nav-about"
                        menuClassName="bg-white text-slate-800 ring-1 ring-slate-200"
                        itemHoverClassName="hover:bg-orange-50 text-slate-800"
                      />
                    ) : (
                      <NavLink
                        key={n.to}
                        to={n.to}
                        end={n.end}
                        onClick={() => setMobileOpen(false)}
                        className={n.highlight ? navLinkAccentClass : navLinkClass}
                      >
                        {n.label}
                      </NavLink>
                    )
                  )}
                  <button
                    type="button"
                    onClick={() => { setMobileOpen(false); setInquiryOpen(true); }}
                    data-testid="ft-nav-apply-mobile"
                    className="col-span-2 mt-2 inline-flex items-center justify-center gap-1.5 bg-[#F4751F] hover:bg-[#E36213] text-white font-semibold text-xs uppercase tracking-wider px-3 py-2 rounded-sm shadow-sm"
                  >
                    Apply Now
                  </button>
                </div>
              )}
            </div>
          </nav>

          {/* Scrolling announcement bar (institute-master driven) */}
          {Array.isArray(inst.scrollers) && inst.scrollers.length > 0 && (
            <ScrollerBar items={inst.scrollers} />
          )}
        </header>

        <main className="flex-1">
          <Outlet />
        </main>

        {/* WhatsApp FAB */}
        {inst.whatsapp_phone && (
          <WhatsAppFab
            phone={inst.whatsapp_phone.replace(/[^0-9]/g, "")}
            message={`Hello ${inst.name || "team"}, I have an admission inquiry.`}
          />
        )}

        {/* Admission Inquiry FAB */}
        <AdmissionInquiryFab onClick={() => setInquiryOpen(true)} />
        <AdmissionInquiryModal
          open={inquiryOpen}
          onClose={() => setInquiryOpen(false)}
          slug={inst.website_slug}
          departments={data.departments || []}
        />

        {/* Footer */}
        <footer className="bg-[#0F1F17] text-white/80 mt-16">
          <div className="max-w-7xl mx-auto px-4 py-12 grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center gap-2 text-[#F4751F] font-semibold">
                <Leaf size={16} strokeWidth={2} />
                <span className="uppercase tracking-[0.25em] text-xs">{inst.name || "MIT Food Tech"}</span>
              </div>
              <p className="mt-3 text-sm leading-relaxed">
                A flagship institution of Gramaudyogik Shikshan Mandal — pioneering food technology education, research, and entrepreneurship in Marathwada.
              </p>
            </div>
            <div>
              <div className="text-white font-semibold mb-3 text-sm">Explore</div>
              <ul className="space-y-2 text-sm">
                {[
                  { to: "about", l: "About" },
                  { to: "vision-mission", l: "Vision & Mission" },
                  { to: "leadership", l: "Leadership" },
                  { to: "departments", l: "Departments" },
                  { to: "admissions", l: "Admissions" },
                  { to: "contact", l: "Contact" },
                ].map((p) => (
                  <li key={p.to}><NavLink to={p.to} className="hover:text-[#F4751F]">{p.l}</NavLink></li>
                ))}
              </ul>
            </div>
            <div>
              <div className="text-white font-semibold mb-3 text-sm">Engage</div>
              <ul className="space-y-2 text-sm">
                <li><NavLink to="campus-engagements" className="hover:text-[#F4751F]">Campus Engagements</NavLink></li>
                <li><NavLink to="committees" className="hover:text-[#F4751F]">Committees &amp; Cells</NavLink></li>
                <li><NavLink to="gallery" className="hover:text-[#F4751F]">Gallery</NavLink></li>
                <li><NavLink to="notices" className="hover:text-[#F4751F]">Notices</NavLink></li>
                <li><NavLink to="links" className="hover:text-[#F4751F]">Important Links</NavLink></li>
                <li><NavLink to="facilities" className="hover:text-[#F4751F]">Facilities</NavLink></li>
              </ul>
            </div>
            <div>
              <div className="text-white font-semibold mb-3 text-sm">Reach Us</div>
              <ul className="space-y-2 text-sm">
                {(inst.address1 || inst.city) && (
                  <li className="flex items-start gap-2"><MapPin size={13} className="mt-0.5 text-[#F4751F] shrink-0" />
                    {[inst.address1, inst.address2, [inst.city, inst.pincode && `– ${inst.pincode}`].filter(Boolean).join(" "), inst.state, inst.country || "INDIA"].filter(Boolean).join(", ")}
                  </li>
                )}
                {(inst.contact_phone || inst.admission_phone) && (
                  <li className="flex items-start gap-2"><Phone size={13} className="mt-0.5 text-[#F4751F]" />
                    {[inst.contact_phone, inst.admission_phone].filter(Boolean).join(" · ")}
                  </li>
                )}
                {inst.email && (
                  <li className="flex items-start gap-2"><Mail size={13} className="mt-0.5 text-[#F4751F]" />
                    <a href={`mailto:${inst.email}`} className="hover:text-[#F4751F]">{inst.email}</a>
                  </li>
                )}
              </ul>
            </div>
          </div>
          <div className="border-t border-white/10 py-4 text-center text-[11px] text-white/60">
            © {new Date().getFullYear()} {inst.name} · Gramaudyogik Shikshan Mandal. All rights reserved.
          </div>
        </footer>
      </div>
    </Ctx.Provider>
  );
}

const navLinkClass = ({ isActive }) =>
  `text-sm px-3 py-2 rounded-md transition-colors whitespace-nowrap ${
    isActive
      ? "bg-[#FFD9A8] text-[#0F1F17] font-semibold shadow-sm"
      : "text-white/90 hover:bg-white/10 hover:text-white"
  }`;

const navLinkAccentClass = ({ isActive }) =>
  `text-sm px-3 py-2 rounded-md transition-colors whitespace-nowrap font-bold uppercase tracking-wider ring-2 ring-[#FFD9A8] shadow-md ${
    isActive
      ? "bg-[#F4751F] text-white"
      : "bg-[#F4751F]/90 text-white hover:bg-[#F4751F]"
  }`;

function DepartmentsDropdown({ departments }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const closeTimer = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const cancelClose = () => { if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; } };
  const scheduleClose = () => { cancelClose(); closeTimer.current = setTimeout(() => setOpen(false), 150); };

  const sorted = [...departments].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  return (
    <div
      ref={wrapRef}
      className="relative"
      onMouseEnter={() => { cancelClose(); setOpen(true); }}
      onMouseLeave={scheduleClose}
      data-testid="ft-nav-departments-wrap"
    >
      <NavLink
        to="departments"
        data-testid="ft-nav-departments"
        className={({ isActive }) => `${navLinkClass({ isActive })} inline-flex items-center gap-1`}
        onClick={() => setOpen(false)}
      >
        Departments
        <ChevronDown size={13} strokeWidth={2} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </NavLink>
      {open && sorted.length > 0 && (
        <div
          className="absolute left-0 top-full pt-1 min-w-[260px] z-50"
          data-testid="ft-nav-departments-menu"
          onMouseEnter={cancelClose}
          onMouseLeave={scheduleClose}
        >
          <div className="bg-white rounded-md shadow-xl ring-1 ring-[#E6E2D6] py-2">
            {sorted.map((d) => (
              <NavLink
                key={d.id}
                to={`departments/${slugify(d.name)}`}
                data-testid={`ft-nav-dept-${d.id}`}
                onClick={() => setOpen(false)}
                className="block px-4 py-2 text-sm text-[#0F1F17] hover:bg-[#FFF1E5]"
              >
                <div className="font-medium">{d.name}</div>
                {d.code && (
                  <div className="text-[10px] uppercase tracking-wider text-[#4D5A52] font-mono mt-0.5">
                    {d.code}
                  </div>
                )}
              </NavLink>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function uniqueTypesWithCount(events) {
  const map = new Map();
  for (const e of events) {
    const label = (e.event_type || "").trim();
    if (!label) continue;
    const slug = slugify(label);
    if (map.has(slug)) {
      map.set(slug, { ...map.get(slug), count: map.get(slug).count + 1 });
    } else {
      map.set(slug, { label, slug, count: 1 });
    }
  }
  return [...map.values()].sort((a, b) => a.label.localeCompare(b.label));
}

function CampusEngagementsDropdown({ events }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const closeTimer = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const cancelClose = () => { if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; } };
  const scheduleClose = () => { cancelClose(); closeTimer.current = setTimeout(() => setOpen(false), 150); };

  const types = uniqueTypesWithCount(events);

  return (
    <div
      ref={wrapRef}
      className="relative"
      onMouseEnter={() => { cancelClose(); setOpen(true); }}
      onMouseLeave={scheduleClose}
      data-testid="ft-nav-engagements-wrap"
    >
      <NavLink
        to="campus-engagements"
        data-testid="ft-nav-campus-engagements"
        className={({ isActive }) => `${navLinkClass({ isActive })} inline-flex items-center gap-1`}
        onClick={() => setOpen(false)}
      >
        Campus Engagements
        {types.length > 0 && (
          <ChevronDown size={13} strokeWidth={2} className={`transition-transform ${open ? "rotate-180" : ""}`} />
        )}
      </NavLink>
      {open && types.length > 0 && (
        <div
          className="absolute left-0 top-full pt-1 min-w-[240px] z-50"
          data-testid="ft-nav-engagements-menu"
          onMouseEnter={cancelClose}
          onMouseLeave={scheduleClose}
        >
          <div className="bg-white rounded-md shadow-xl ring-1 ring-[#E6E2D6] py-2">
            <NavLink
              to="campus-engagements"
              data-testid="ft-nav-engagements-all"
              onClick={() => setOpen(false)}
              className="block px-4 py-2 text-sm text-[#0F1F17] hover:bg-[#FFF1E5] border-b border-[#F4E9D6]"
            >
              <div className="font-medium">All Stories</div>
              <div className="text-[10px] uppercase tracking-wider text-[#4D5A52] mt-0.5">Show every type</div>
            </NavLink>
            {types.map((t) => (
              <NavLink
                key={t.slug}
                to={`campus-engagements/${t.slug}`}
                data-testid={`ft-nav-engagement-type-${t.slug}`}
                onClick={() => setOpen(false)}
                className="block px-4 py-2 text-sm text-[#0F1F17] hover:bg-[#FFF1E5]"
              >
                <div className="flex items-center justify-between gap-3">
                  <span className="font-medium">{t.label}</span>
                  <span className="text-[10px] uppercase tracking-wider text-[#4D5A52] font-mono">{t.count}</span>
                </div>
              </NavLink>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function WhatsAppFab({ phone, message }) {
  const href = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Chat on WhatsApp for admission inquiry"
      data-testid="ft-whatsapp-fab"
      className="fixed z-40 bottom-24 right-5 sm:bottom-28 sm:right-7 flex items-center gap-2 group"
    >
      <span className="absolute inline-flex h-14 w-14 rounded-full bg-green-500 opacity-40 animate-ping pointer-events-none right-0 bottom-0" />
      <span className="hidden sm:inline-flex bg-white text-[#0F1F17] text-xs font-semibold px-3 py-2 rounded-full shadow-md ring-1 ring-[#E6E2D6] opacity-0 group-hover:opacity-100 transition-opacity">
        Admission inquiry
      </span>
      <span className="relative inline-flex items-center justify-center h-14 w-14 rounded-full bg-[#25D366] hover:bg-[#1ebe5d] text-white shadow-lg ring-4 ring-white transition-transform group-hover:scale-105">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-7 h-7" aria-hidden="true">
          <path d="M20.52 3.48A11.78 11.78 0 0 0 12.06 0C5.5 0 .15 5.34.15 11.9c0 2.1.55 4.15 1.6 5.96L0 24l6.32-1.66a11.86 11.86 0 0 0 5.73 1.46h.01c6.56 0 11.91-5.34 11.91-11.9 0-3.18-1.24-6.17-3.45-8.42ZM12.06 21.8h-.01a9.85 9.85 0 0 1-5.03-1.38l-.36-.21-3.75.98 1-3.65-.23-.37a9.86 9.86 0 0 1-1.51-5.27c0-5.45 4.44-9.89 9.9-9.89 2.64 0 5.13 1.03 7 2.9a9.87 9.87 0 0 1 2.9 7c0 5.46-4.44 9.89-9.9 9.89Zm5.43-7.4c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.66.15-.2.3-.77.97-.95 1.17-.17.2-.35.22-.65.07-.3-.15-1.25-.46-2.39-1.47-.88-.78-1.48-1.75-1.65-2.05-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.66-1.6-.9-2.18-.24-.58-.48-.5-.66-.5l-.56-.01c-.2 0-.5.07-.76.37-.27.3-1.02.99-1.02 2.42 0 1.43 1.04 2.81 1.18 3 .15.2 2.06 3.15 5 4.42.7.3 1.24.48 1.66.62.7.22 1.34.19 1.84.12.56-.08 1.76-.72 2-1.42.25-.7.25-1.3.17-1.42-.07-.13-.27-.2-.57-.35Z" />
        </svg>
      </span>
    </a>
  );
}

function AdmissionInquiryFab({ onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Open admission inquiry form"
      data-testid="ft-admission-inquiry-fab"
      className="fixed z-40 bottom-5 right-5 sm:bottom-7 sm:right-7 inline-flex items-center gap-2 group"
    >
      <span className="hidden sm:inline-flex bg-white text-[#0F1F17] text-xs font-semibold px-3 py-2 rounded-full shadow-md ring-1 ring-[#E6E2D6] opacity-0 group-hover:opacity-100 transition-opacity">
        Contact Our Admission Team
      </span>
      <span className="relative inline-flex items-center justify-center h-14 w-14 rounded-full bg-gradient-to-br from-[#F4751F] to-[#C95507] hover:from-[#E36213] hover:to-[#A0440A] text-white shadow-lg ring-4 ring-white transition-transform group-hover:scale-105">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-7 h-7" aria-hidden="true">
          <path d="M22 10v6" />
          <path d="M2 10l10-5 10 5-10 5z" />
          <path d="M6 12v5c3 3 9 3 12 0v-5" />
        </svg>
      </span>
    </button>
  );
}

function ScrollerBar({ items }) {
  const list = items || [];
  if (list.length === 0) return null;
  const renderSegment = (s, i) => {
    const inner = (
      <span className="inline-block whitespace-nowrap px-3">
        <span className="opacity-80 mr-2 align-middle">▸</span>
        {s.text}
      </span>
    );
    if (s.url) {
      return (
        <a
          key={`${s.id}-${i}`}
          href={s.url}
          target="_blank"
          rel="noopener noreferrer"
          className="hover:underline focus:underline outline-none"
          data-testid={`ft-scroller-link-${s.id}`}
        >
          {inner}
        </a>
      );
    }
    return (
      <span key={`${s.id}-${i}`} data-testid={`ft-scroller-text-${s.id}`}>
        {inner}
      </span>
    );
  };
  const duration = Math.max(20, Math.min(60, list.length * 12));
  return (
    <div
      className="bg-gradient-to-r from-[#F4751F] to-[#C95507] text-white border-y border-[#F4751F]/50 overflow-hidden"
      data-testid="ft-scroller-bar"
    >
      <style>{`
        @keyframes ft-marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        [data-testid="ft-scroller-bar"]:hover [data-testid="ft-scroller-track"] {
          animation-play-state: paused !important;
        }
      `}</style>
      <div className="max-w-7xl mx-auto px-3 py-1.5 flex items-center gap-3">
        <span className="hidden sm:inline-flex text-[10px] tracking-[0.25em] uppercase font-bold bg-white/20 px-2 py-0.5 rounded-sm shrink-0">
          Announcement
        </span>
        <div className="flex-1 overflow-hidden">
          <span
            data-testid="ft-scroller-track"
            className="inline-block whitespace-nowrap text-sm font-medium"
            style={{ animation: `ft-marquee ${duration}s linear infinite` }}
          >
            {list.map(renderSegment)}
            {list.map((s, i) => renderSegment(s, `copy-${i}`))}
          </span>
        </div>
      </div>
    </div>
  );
}
